import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import { GYMNASTICS_EVENTS } from '../utils/constants.js';

// Model-dev label VERIFICATION queue (superadmin/staff). Auto- and coach-labeled
// clips are provisional until a human confirms them here — only verified labels
// should feed model training. Correct-and-verify in one step; bulk verify too.
const LANDING_TYPES = ['Stick', 'Hop/Step', 'Over-rotated', 'Short Landing'];
const ENTRIES = ['Direct', 'Piked', 'Layouts', 'Arabesque'];

export default function LabelVerification() {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('review'); // 'review' | 'verified' | 'all'
  const [event, setEvent] = useState('');
  const [sort, setSort] = useState('confidence'); // 'confidence' | 'newest'
  const [selected, setSelected] = useState(() => new Set());
  const [edits, setEdits] = useState({}); // { [id]: { landing_type, entry } }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (status === 'review') p.set('verified', 'false');
      else if (status === 'verified') p.set('verified', 'true');
      if (event) p.set('event', event);
      p.set('sort', sort === 'confidence' ? 'confidence' : 'newest');
      p.set('limit', '100');
      const { data } = await api.get(`${API_BASE_URL}/api/athlete-landings?${p.toString()}`);
      setRows(Array.isArray(data) ? data : []);
      setSelected(new Set());
      setEdits({});
    } catch {
      setRows([]);
    } finally { setLoading(false); }
  }, [status, event, sort]);
  useEffect(() => { load(); }, [load]);

  const editOf = (r) => edits[r.id] || { landing_type: r.landing_type || 'Stick', entry: r.entry || 'Direct' };
  const setEdit = (id, patch) => setEdits(e => ({ ...e, [id]: { ...editOf({ id, ...rows.find(r => r.id === id) }), ...patch } }));
  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = rows.length > 0 && rows.every(r => selected.has(r.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map(r => r.id)));

  const verify = async (ids, { unverify = false } = {}) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const corrections = unverify ? [] : ids.filter(id => edits[id]).map(id => ({ landing_id: id, ...edits[id] }));
      const { data } = await api.post(`${API_BASE_URL}/api/verify-landings`, { landing_ids: ids, corrections, unverify });
      toast.success(unverify ? `Sent ${ids.length} back to review.` : `Verified ${data.verified} clip(s)${data.relabeled ? ` (${data.relabeled} corrected)` : ''}.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Verify failed.');
    } finally { setBusy(false); }
  };

  const box = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1rem 1.25rem' };
  const sel = { padding: '0.4rem 0.6rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.85rem' };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '1.5rem 1rem 4rem' }}>
      <h1 style={{ fontSize: '1.4rem', margin: '0 0 .25rem' }}>✅ Label Verification</h1>
      <p style={{ color: '#6b7280', margin: '0 0 1rem', fontSize: '.9rem' }}>
        Confirm or correct each clip's label. Only <strong>verified</strong> labels feed model training.
      </p>

      {/* Filters */}
      <div style={{ ...box, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 14 }}>
        <label style={{ fontSize: '.8rem', color: '#374151' }}>Status&nbsp;
          <select value={status} onChange={e => setStatus(e.target.value)} style={sel}>
            <option value="review">Needs review</option>
            <option value="verified">Verified</option>
            <option value="all">All</option>
          </select>
        </label>
        <label style={{ fontSize: '.8rem', color: '#374151' }}>Event&nbsp;
          <select value={event} onChange={e => setEvent(e.target.value)} style={sel}>
            <option value="">All events</option>
            {GYMNASTICS_EVENTS.map(ev => <option key={ev.value} value={ev.value}>{ev.label}</option>)}
          </select>
        </label>
        <label style={{ fontSize: '.8rem', color: '#374151' }}>Sort&nbsp;
          <select value={sort} onChange={e => setSort(e.target.value)} style={sel}>
            <option value="confidence">Lowest confidence first</option>
            <option value="newest">Newest first</option>
          </select>
        </label>
        <button onClick={load} disabled={loading} style={{ ...sel, cursor: 'pointer', background: '#fff' }}>↻ Refresh</button>
        <span style={{ marginLeft: 'auto', fontSize: '.8rem', color: '#6b7280' }}>{rows.length} clip{rows.length === 1 ? '' : 's'}</span>
      </div>

      {/* Bulk bar */}
      {status !== 'verified' && rows.length > 0 && (
        <div style={{ ...box, display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, padding: '.6rem 1rem' }}>
          <label style={{ fontSize: '.85rem', display: 'inline-flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={allSelected} onChange={toggleAll} /> Select all
          </label>
          <button
            onClick={() => verify([...selected])}
            disabled={busy || selected.size === 0}
            style={{ marginLeft: 'auto', padding: '.5rem 1rem', borderRadius: 8, border: 'none', fontWeight: 700, fontSize: '.85rem', cursor: selected.size ? 'pointer' : 'not-allowed', background: selected.size ? '#16a34a' : '#cbd5e1', color: '#fff' }}>
            ✅ Verify {selected.size} selected
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ ...box, color: '#9ca3af' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ ...box, color: '#6b7280' }}>
          {status === 'review'
            ? 'Nothing to review. Labels appear here once clips are analyzed (nightly batch) or labeled.'
            : 'No clips match.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rows.map(r => {
            const e = editOf(r);
            const changed = !!edits[r.id] && (e.landing_type !== r.landing_type || e.entry !== r.entry);
            return (
              <div key={r.id} style={{ ...box, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start', borderColor: r.verified ? '#bbf7d0' : '#e5e7eb' }}>
                {status !== 'verified' && (
                  <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} style={{ marginTop: 6 }} />
                )}
                <video src={r.video_id ? `/videos/${r.video_id}` : undefined} controls playsInline
                  style={{ width: 200, maxWidth: '100%', borderRadius: 8, background: '#000', flexShrink: 0 }} />
                <div style={{ flex: '1 1 260px', minWidth: 240 }}>
                  <div style={{ fontWeight: 700 }}>{r.athlete_name || `Athlete ${r.athlete_id}`}
                    <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '.85rem' }}> · {r.event || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '6px 0', fontSize: '.75rem' }}>
                    <span style={{ padding: '1px 7px', borderRadius: 999, background: r.labeled_by ? '#eef2ff' : '#f1f5f9', color: r.labeled_by ? '#3730a3' : '#475569' }}>
                      {r.labeled_by ? `👤 ${r.labeled_by_name || 'coach'}` : '🤖 auto'}
                    </span>
                    {r.model_confidence != null && <span style={{ color: '#6b7280' }}>conf {Number(r.model_confidence).toFixed(2)}</span>}
                    {r.verified ? <span style={{ color: '#15803d', fontWeight: 700 }}>✅ verified{r.verified_by_name ? ` · ${r.verified_by_name}` : ''}</span> : null}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <label style={{ fontSize: '.75rem', color: '#374151' }}>Landing&nbsp;
                      <select value={e.landing_type} onChange={ev => setEdit(r.id, { landing_type: ev.target.value })} style={sel}>
                        {LANDING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </label>
                    <label style={{ fontSize: '.75rem', color: '#374151' }}>Entry&nbsp;
                      <select value={e.entry} onChange={ev => setEdit(r.id, { entry: ev.target.value })} style={sel}>
                        {ENTRIES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </label>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {r.verified ? (
                    <button onClick={() => verify([r.id], { unverify: true })} disabled={busy}
                      style={{ padding: '.45rem .9rem', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: '.82rem' }}>↩ Un-verify</button>
                  ) : (
                    <button onClick={() => verify([r.id])} disabled={busy}
                      style={{ padding: '.45rem .9rem', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '.82rem' }}>
                      {changed ? '✅ Correct & verify' : '✅ Verify'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
